import Vue from 'vue'
import Config from '@/config'
import ConversationService from '@/services/ConversationService'
import GiftCardService from '@/services/GiftCardService'
import NoteService from '@/services/NoteService'
import {
  parseTicketCreatedMessage,
  ticketNoteText,
  ticketPlaceholderId
} from '@/lib/ticket-created-message'
import moment from 'moment-timezone'

// Sort key for the conversation list. `lastCommunication` is absent whenever
// the api nulled it for a blocked customer, and on any conversation it never
// populated, so fall back to `updated` to give such a row a defined position.
// Note this is a client-side choice, not a mirror of the server: mongo sorts
// missing values last under a -1 sort rather than falling back to another
// field, so a blocked row can shift position as later pages arrive. The set of
// rows is still complete, which is what matters here.
//
// Normalise before comparing: a Date stringifies to "Wed Sep 09 2026 ...",
// which would compare by weekday name and scramble the order outright.
const effectiveDate = (conversation) => {
  const raw = conversation.lastCommunication?.date || conversation.updated
  if (!raw) return ''
  const parsed = moment(raw)
  return parsed.isValid() ? parsed.toISOString() : ''
}

// Lower bound for the list window. A null/0 window means All time, which sends no
// bound at all rather than a very old one, so the request stays byte-identical
// to an unwindowed one.
export const computeSince = (dateWindowDays) =>
  dateWindowDays ? moment().subtract(dateWindowDays, 'days').toISOString() : null

// Decorate, sort, undecorate. Comparing lazily would parse two moments per
// comparison, and this mutation runs on every websocket conversation update.
const sortByEffectiveDate = (conversations) => {
  const keys = new Map(conversations.map((c) => [c, effectiveDate(c)]))
  return conversations.sort(
    (a, b) => keys.get(b).localeCompare(keys.get(a)) || String(b._id).localeCompare(String(a._id))
  )
}

const defaultState = () => ({
  activeConversation: null,
  activeConversationChannelId: null,
  conversationIds: [],
  conversationsById: {},
  conversations: [],
  // dateWindowDays null means All time, which is the default: first load is
  // unbounded and an operator opts into a window.
  filters: { resolved: false, dateWindowDays: null },
  conversationLimit: 50,
  skip: 0,
  nextPage: 1,
  // Frozen at list reset so load-more pages share one lower bound. Recomputing
  // it mid-scroll would shift the skip/limit page boundaries under the operator.
  frozenSince: null,
  searchedConversations: [],
  responseTimes: [],
  listExhausted: false,
  activeConversationLoading: false,
  fetchingResponseTimes: false,
  drawerLocationId: null,
  isMobileView: false,
  showDetailsPane: true,
  unresolvedCount: 0,
  openRates: {},

  // this is just a temp variable for debugging. remove once api spikes are done
  fetchTimestamp: null,
  setSpecificConversationTimestamp: null
})

export default {
  namespaced: true,
  state: {
    ...defaultState(),
    isWebview: false,
    isDeeplink: false,
    searchString: undefined
  },
  getters: {
    selectConversationIds: (state) => state.conversationIds,
    selectConversationById: (state) => (conversationId) => state.conversationsById[conversationId],
    selectConversations: (state) => state.conversations,
    selectSearchedConversations: (state) => state.searchedConversations,
    selectFilters: (state) => state.filters,
    selectDateWindowDays: (state) => state.filters.dateWindowDays ?? null,
    selectFrozenSince: (state) => state.frozenSince,
    selectConversationLimit: (state) => state.conversationLimit,
    selectSkip: (state) => state.skip,
    selectNextPage: (state) => state.nextPage,
    selectResponseTimes: (state) => state.responseTimes,
    selectResponseTimeById: (state) => (conversationId) => state.responseTimes[conversationId],
    selectListExhausted: (state) => state.listExhausted,
    selectActiveConversation: (state) => state.activeConversation,
    selectActiveConversationId: (state) => state.activeConversation?._id,
    selectActiveConversationCustomer: (state) => state.activeConversation?.customer,
    selectActiveConversationLocation: (state) => state.activeConversation?.location,
    selectActiveConversationCompanyId: (state) => state.activeConversation?.company,
    selectActiveConversationLoading: (state) => state.activeConversationLoading,
    selectFetchingResponseTimes: (state) => state.fetchingResponseTimes,
    selectDrawerLocationId: (state) => state.drawerLocationId,
    selectIsMobileView: (state) => state.isMobileView,
    selectShowDetailsPane: (state) => state.showDetailsPane,
    selectUnresolvedCount: (state) => state.unresolvedCount,
    selectOpenRates: (state) => state.openRates,
    selectIsSearching: (state) => (state.searchString ? true : false),
    selectActiveConversationChannelId: (state) => state.activeConversationChannelId
  },
  mutations: {
    SET_SKIP(state, skip) {
      state.skip = skip
    },
    SET_NEXT_PAGE(state, nextPage) {
      state.nextPage = nextPage
    },
    SET_SEARCHED_CONVERSATIONS(state, searchedConversations) {
      state.searchedConversations = [...state.searchedConversations, ...searchedConversations]
    },
    SET_CONVERSATIONS(state, conversations) {
      const newConversationIds = conversations.map((conversation) => conversation._id)
      let newConversations = [
        ...state.conversations.filter(
          (conversation) => !newConversationIds.includes(conversation._id)
        ),
        ...conversations
      ]

      // there will be no filters for the winback drawers so don't run this next 'if'
      if (!state.filters.search && Object.keys(state.filters).length) {
        // `resolved` must not enter the sort key. Sorting by it partitions the
        // All tab into two blocks instead of one date-ordered list, and it
        // disagrees with the server order, which moves rows across the
        // pagination cut between the page the server built and the page we
        // render.
        sortByEffectiveDate(newConversations)
      }

      if (!state.isWebview && !state.isDeeplink) {
        if (state.filters.resolved) {
          newConversations = newConversations.filter((c) => c.resolved)
        } else if (state.filters.resolved === false) {
          newConversations = newConversations.filter((c) => !c.resolved)
        }
      }

      // Not redundant with the server's scoping. The paged list is scoped
      // server-side, but fetchConversation and setSpecificConversation build
      // their own payloads against every location the user can see, then commit
      // here, so a live update for another location would otherwise land in a
      // location-filtered list. Resolve the id defensively: location can arrive
      // unpopulated as a bare id, and an unguarded deref would throw and
      // abandon the whole mutation partway through.
      if (state.filters.locationIds?.length) {
        newConversations = newConversations.filter((c) =>
          state.filters.locationIds.includes(c.location?._id || c.location)
        )
      }

      // narrow before indexing, or the indexes retain rows the list does not
      // render and selectConversationById hands back a hidden conversation
      state.conversations = newConversations
      state.conversationIds = newConversations.map((c) => c._id)
      state.conversationsById = newConversations.reduce((acc, curr) => {
        acc[curr._id] = curr
        return acc
      }, {})

      if (state.activeConversation) {
        const updated = state.conversationsById[state.activeConversation._id]
        if (updated) {
          state.activeConversation = updated
        }
      }
    },
    SET_FILTERS(state, filters) {
      state.filters = filters
    },
    SET_FROZEN_SINCE(state, since) {
      state.frozenSince = since
    },
    SET_RESPONSE_TIMES(state, responseTimes) {
      let newResponseTimes = { ...state.responseTimes }

      responseTimes.forEach((response) => {
        if (response) {
          newResponseTimes[response.conversationId] = response
        }
      })

      state.responseTimes = newResponseTimes
    },
    RESET_CONVERSATIONS(state) {
      state.conversationIds = []
      state.conversationsById = {}
      state.conversations = []
      state.listExhausted = false
      state.skip = 0
      state.nextPage = 1
      state.searchedConversations = []
    },
    RESET_STATE(state) {
      Object.assign(state, defaultState())
    },
    SET_LIST_EXHAUSTED(state, exhausted) {
      state.listExhausted = exhausted
    },
    SET_ACTIVE_CONVERSATION(state, conversation) {
      state.activeConversation = conversation
    },
    SET_ACTIVE_CONVERSATION_LOADING(state, status) {
      state.activeConversationLoading = status
    },
    ADD_USER_OPEN(state, userId) {
      state.activeConversation.opened.push(userId)
    },
    SET_FETCHING_RESPONSE_TIMES(state, status) {
      state.fetchResponseTimes = status
    },
    SET_DRAWER_LOCATION_ID(state, locationId) {
      state.drawerLocationId = locationId
    },
    SET_IS_MOBILE_VIEW(state, status) {
      state.isMobileView = status
    },
    SET_SHOW_DETAILS_PANE(state, status) {
      state.showDetailsPane = status
    },
    SET_CONVERSATION_COUNT(state, count) {
      state.unresolvedCount = count
    },
    SET_IS_WEBVIEW(state, status) {
      state.isWebview = status
    },
    SET_IS_DEEPLINK(state, status) {
      state.isDeeplink = status
    },
    SET_OPEN_RATES(state, openRates) {
      state.openRates = openRates
    },
    SET_SEARCH_STRING(state, searchString) {
      state.searchString = searchString
    },
    SET_ACTIVE_CONVERSATION_CHANNEL_ID(state, conversationId) {
      state.activeConversationChannelId = conversationId
    }
  },
  actions: {
    async resetAllConversationData({ commit, dispatch }) {
      commit('RESET_STATE')
      await dispatch('timelineV2/resetAllTimelineData', null, { root: true })
    },

    async refreshActiveConversation({ dispatch, getters }) {
      await dispatch('setActiveConversation', getters.selectActiveConversation)
    },

    async fetchConversations({ state, commit, rootGetters, getters, dispatch }, { reset, trace }) {
      // for debugging api spikes: remove once resolved
      if (state.fetchTimestamp && moment().diff(state.fetchTimestamp, 'seconds', true) < 0.5) {
        return
      }
      state.fetchTimestamp = moment()
      // end debugging code
      if (!rootGetters['company/activeCompany']) return

      // Freeze the window's lower bound at reset and reuse it for every
      // load-more page. Held in a local rather than read back through the getter
      // so the value this request sends is the value that was just committed,
      // with no dependency on when the store republishes it.
      let since = getters.selectFrozenSince
      if (reset) {
        commit('RESET_CONVERSATIONS')
        since = computeSince(getters.selectFilters.dateWindowDays)
        commit('SET_FROZEN_SINCE', since)
      }

      let companies =
        rootGetters['company/activeCompany']._id == 'ALL_COMPANIES'
          ? rootGetters['company/companies']
          : [rootGetters['company/activeCompany']]

      let response = await ConversationService.fetchConversations({
        ...(getters.selectFilters.search ? { search: getters.selectFilters.search } : {}),
        limit: getters.selectConversationLimit,
        skip: getters.selectSkip,
        page: getters.selectNextPage,
        filters: {
          companyIds: companies.map((c) => c._id),
          ...(getters.selectFilters.locationIds?.length
            ? { locationIds: getters.selectFilters.locationIds }
            : {}),
          // Omitted entirely when the window is All, matching how the All tab
          // omits `resolved`. Search is deliberately unbounded: it is how an
          // operator reaches an old item on purpose.
          ...(since && !getters.selectFilters.search ? { effectiveDateSince: since } : {}),
          resolved: getters.selectFilters.resolved
        }
      })

      const conversationList = response.body.data.conversations.filter((c) => c.customer)

      if (conversationList.length) {
        dispatch('fetchResponseTimes', conversationList)
      }

      if (getters.selectFilters.search) {
        commit('SET_SEARCHED_CONVERSATIONS', conversationList)
        if (conversationList.length && response.body.data.metadata.nextPage) {
          commit('SET_NEXT_PAGE', response.body.data.metadata.nextPage)
        } else {
          commit('SET_LIST_EXHAUSTED', true)
        }
      } else {
        // a short page is not the last page. The api applies its limit first and
        // only then drops rows whose customer reference is missing, so a full
        // page of 50 routinely arrives as 47. Latching on that froze the list
        // permanently and made the ui claim there was nothing left to load.
        // Only an empty page proves the end.
        if (!response.body.data.conversations.length) {
          commit('SET_LIST_EXHAUSTED', true)
        }
        // store list of ids and conversations
        // increase skip by the requested limit rather than by the rows we got:
        // the api pages with skip/limit over the matched set and drops rows
        // afterwards, so it consumed a full limit of documents even on a short
        // page. Advancing by the rows received would re-request the dropped ones.
        commit('SET_CONVERSATIONS', conversationList)
        commit('SET_SKIP', getters.selectSkip + getters.selectConversationLimit)
      }
    },

    async setUnresolvedCount({ commit, rootGetters }) {
      if (!rootGetters['company/activeCompany']) return
      const companies =
        rootGetters['company/activeCompany']._id == 'ALL_COMPANIES'
          ? rootGetters['company/companies']
          : [rootGetters['company/activeCompany']]

      const result = await ConversationService.fetchCount({
        filters: {
          companyIds: companies.map((c) => c._id)
        }
      })

      commit('SET_CONVERSATION_COUNT', result.data.data.count)
    },

    updateFilters({ commit }, filters) {
      commit('SET_FILTERS', filters)
    },

    updateDrawerLocationId({ commit }, locationId) {
      commit('SET_DRAWER_LOCATION_ID', locationId)
    },

    async fetchResponseTimes({ commit }, conversations) {
      commit('SET_FETCHING_RESPONSE_TIMES', true)
      const response = await ConversationService.fetchResponseTimes({
        conversationIds: conversations.filter((c) => c.lastCommunication).map((c) => c._id)
      })

      commit('SET_RESPONSE_TIMES', response.body.data.responseTimes)
      commit('SET_FETCHING_RESPONSE_TIMES', false)
    },

    // Idempotent helper — subscribe to a conversation's presence channel, unsubscribing
    // the prior one first. Calling with the same id that is already active is a no-op.
    // Pass conversationId=null to clear without subscribing (placeholder / closed state).
    // Fail-soft: presence-channel errors are non-fatal — core conversation render must
    // never be blocked by a subscribe failure or a pending pusher promise.
    async _bindConversationPresence({ commit, dispatch, getters }, { conversationId }) {
      const priorId = getters.selectActiveConversationChannelId
      if (!conversationId) {
        // Clear path: unsubscribe prior (if any) and clear the stored id
        if (priorId) {
          try {
            await dispatch(
              'websocketV2/conversationChannelUnsubscribe',
              { conversationId: priorId },
              { root: true }
            )
          } catch (err) {
            console.warn(
              '[conversationV2] conversationChannelUnsubscribe failed (clear path):',
              err
            )
          }
        }
        commit('SET_ACTIVE_CONVERSATION_CHANNEL_ID', null)
        return
      }
      // Same-id path: already subscribed — no-op
      if (conversationId === priorId) return
      // Switch path: unsubscribe old, subscribe new
      if (priorId) {
        try {
          await dispatch(
            'websocketV2/conversationChannelUnsubscribe',
            { conversationId: priorId },
            { root: true }
          )
        } catch (err) {
          console.warn('[conversationV2] conversationChannelUnsubscribe failed (switch path):', err)
        }
      }
      try {
        await dispatch(
          'websocketV2/conversationChannelSubscribe',
          { conversationId },
          { root: true }
        )
        // Only record the channel id after a successful subscribe
        commit('SET_ACTIVE_CONVERSATION_CHANNEL_ID', conversationId)
      } catch (err) {
        console.warn(
          '[conversationV2] conversationChannelSubscribe failed — live push unavailable for this conversation:',
          err
        )
        // Do NOT commit SET_ACTIVE_CONVERSATION_CHANNEL_ID: we are not actually subscribed
      }
    },

    async setActiveConversation({ commit, dispatch, getters, rootGetters }, conversation) {
      commit('SET_ACTIVE_CONVERSATION_LOADING', true)
      await dispatch('timelineV2/clearTimelineData', null, { root: true })
      let matchedConversation
      if (conversation?._id) {
        matchedConversation = getters.selectFilters.search
          ? conversation
          : getters.selectConversationById(conversation._id)
        commit('SET_ACTIVE_CONVERSATION', matchedConversation)
      } else {
        let conversations = await dispatch('fetchConversation', {
          conversation,
          trace: 'setActiveConversation'
        })
        if (conversations.length) {
          matchedConversation = conversations.find(
            (conversation) => conversations[0]._id === conversation._id
          )
        }
      }

      if (!matchedConversation && conversation?.customer?._id) {
        commit('SET_ACTIVE_CONVERSATION', {
          customer: { _id: conversation.customer._id },
          company: { _id: rootGetters['company/activeCompany']._id }
        })
      } else {
        commit('SET_ACTIVE_CONVERSATION', matchedConversation)
        await dispatch('timelineV2/fetchTimeline', null, { root: true })
      }

      // Wire per-conversation presence channel
      await dispatch('_bindConversationPresence', { conversationId: conversation?._id ?? null })

      commit('SET_ACTIVE_CONVERSATION_LOADING', false)
    },

    async closeActiveConversationChannel({ commit, dispatch, getters }) {
      const channelId = getters.selectActiveConversationChannelId
      if (!channelId) return
      await dispatch(
        'websocketV2/conversationChannelUnsubscribe',
        { conversationId: channelId },
        { root: true }
      )
      commit('SET_ACTIVE_CONVERSATION_CHANNEL_ID', null)
    },

    async setSpecificConversation(
      { commit, dispatch, getters, rootGetters, state },
      { filters, trace }
    ) {
      // for debugging api spikes: remove once resolved
      if (
        state.setSpecificConversationTimestamp &&
        moment().diff(state.setSpecificConversationTimestamp, 'seconds', true) < 0.5
      ) {
        return
      }
      state.setSpecificConversationTimestamp = moment()
      // end debugging code

      const { customer, locationId, companyId } = filters
      const customerId = customer?._id
      if (!customerId) return
      commit('SET_ACTIVE_CONVERSATION_LOADING', true)

      const userActiveLocationsIds = rootGetters['location/selectActiveLocations'].map((l) => l._id)
      const customerActiveCompanyLocations = customer?.locations?.filter((l) =>
        userActiveLocationsIds.includes(l)
      )

      // fetch the conversation
      let payload = {
        filters: {
          companyIds: companyId ? [companyId] : [rootGetters['company/activeCompany']._id],
          locationIds:
            locationId || customerActiveCompanyLocations?.length === 1
              ? [locationId || customerActiveCompanyLocations[0]]
              : rootGetters['location/selectActiveLocations'].map((location) => location._id),
          customerIds: [customerId],
          requireLastCommunication: false
        },
        limit: getters.selectConversationLimit
      }

      const response = await ConversationService.fetchConversations(payload)
      const { conversations } = response.body.data

      // if no location is specified and there are multiple location options then use placehold conversation
      if (!locationId && !conversations?.length && customerActiveCompanyLocations?.length !== 1) {
        commit('SET_ACTIVE_CONVERSATION', {
          customer: { _id: customerId },
          company: { _id: rootGetters['company/activeCompany']._id }
        })
        commit('SET_ACTIVE_CONVERSATION_LOADING', false)
        return
      }

      // if no conversation for specified location, create one
      else if (
        (locationId || customerActiveCompanyLocations?.length === 1) &&
        !conversations?.length
      ) {
        const resolvedLocationId = locationId || customerActiveCompanyLocations[0]
        const newConversation = await dispatch('createConversation', {
          locationId: resolvedLocationId,
          customerId: customerId,
          companyId: companyId || rootGetters['company/activeCompany']._id
        })
        // createConversation resolves the unpopulated server document: `location` and `customer`
        // come back as bare id strings, while readers expect the shapes a fetched conversation
        // carries — `location._id` and a populated `customer`.
        const activeConversation = {
          ...newConversation,
          customer,
          location: { _id: resolvedLocationId }
        }
        commit('SET_CONVERSATIONS', [activeConversation])
        commit('SET_ACTIVE_CONVERSATION', activeConversation)
        await dispatch('_bindConversationPresence', {
          conversationId: newConversation?._id ?? null
        })
      } else {
        commit('SET_CONVERSATIONS', conversations)
        commit('SET_ACTIVE_CONVERSATION', conversations[0])
        await dispatch('_bindConversationPresence', {
          conversationId: conversations[0]?._id ?? null
        })
      }
      await dispatch('timelineV2/fetchTimeline', null, { root: true })
      dispatch('timelineV2/scrollToLastEvent', null, { root: true })

      commit('SET_ACTIVE_CONVERSATION_LOADING', false)
    },

    // Deep-link entry: sets the conversation active. payload is a conversationId
    // string, or { conversationId, skipTimeline } where skipTimeline defers the
    // timeline fetch (e.g. when called before user locations are loaded).
    async fetchSpecificConversation({ commit, dispatch, getters, rootGetters }, payload) {
      const isOptions = payload !== null && typeof payload === 'object'
      const conversationId = isOptions ? payload.conversationId : payload
      const skipTimeline = isOptions ? !!payload.skipTimeline : false
      const response = await ConversationService.fetchConversation(conversationId)

      const conversation = response.body.data
      if (conversation) {
        // check if company is different from active company, change companies if so
        if (
          !rootGetters['company/activeCompany'] ||
          rootGetters['company/activeCompany']._id !== conversation.company
        ) {
          const company = rootGetters['company/selectCompany'](conversation.company)
          if (process.env.NODE_ENV !== 'headless') {
            window.parent.postMessage(
              { company, event: 'CHANGE_ACTIVE_COMPANY' },
              Config.baseAppUrl
            )
          }
          // Set conversation before company switch so template bindings don't
          // crash accessing ._id on undefined getters during the re-render that
          // the company change triggers synchronously.
          commit('SET_ACTIVE_CONVERSATION', conversation)
          await dispatch('company/setActiveCompany', company, { root: true })
          // Skip the timeline fetch — the company switch triggers a WinbackView
          // remount which will call parseRouteDetails again with correct context.
          // Fetching here would use stale locationIds and overwrite correct data.
          return
        }
        commit('SET_ACTIVE_CONVERSATION', conversation)
        await dispatch('_bindConversationPresence', { conversationId: conversation._id })
        // Skipped on the early deep-link call (locations not loaded → empty
        // locationIds). Clear before fetching, mirroring setActiveConversation.
        if (!skipTimeline) {
          await dispatch('timelineV2/clearTimelineData', null, { root: true })
          await dispatch('timelineV2/fetchTimeline', null, { root: true })
        }
      }
    },

    async fetchConversation(
      { state, commit, dispatch, getters, rootGetters },
      { conversation, trace }
    ) {
      let payload = {
        filters: {
          companyIds: rootGetters['dataCore/selectActiveCompanyIds'],
          locationIds: rootGetters['location/selectActiveLocations'].map((location) => location._id)
        },
        limit: getters.selectConversationLimit
      }

      if (conversation._id) {
        payload.filters.conversationIds = [conversation._id]
      } else {
        payload.filters.customerIds = [conversation.customer._id]
      }

      const response = await ConversationService.fetchConversations(payload)

      if (response.body.success !== true) {
        throw response.body
      }

      let { conversations } = response.body.data

      if (conversations.length) {
        dispatch('fetchResponseTimes', conversations)
      }

      commit('SET_CONVERSATIONS', conversations)

      return conversations
    },

    async open({ commit, rootState }, { conversationId }) {
      if (!conversationId) return
      const response = await ConversationService.markOpen({ conversationId })
      commit('ADD_USER_OPEN', `${rootState.user.onlineUser._id}`)
    },

    async createConversation({ dispatch, commit, getters }, { customerId, companyId, locationId }) {
      const response = await ConversationService.createConversation({
        customer: customerId,
        location: locationId,
        company: companyId
      })

      const conversation = response.body.data
      let newConversation = { ...getters.selectActiveConversation }
      if (newConversation && !newConversation._id) {
        newConversation._id = conversation._id
        // The list copy needs the shaped location too. SET_CONVERSATIONS re-syncs
        // activeConversation from conversationsById whenever a later commit carries this id, so
        // a bare-string location here would silently undo the commit below on the next update.
        const activeLocation = { _id: locationId }
        commit('SET_CONVERSATIONS', [
          { ...newConversation, ...conversation, location: activeLocation }
        ])
        commit('SET_ACTIVE_CONVERSATION', { ...newConversation, location: activeLocation })
        await dispatch('_bindConversationPresence', { conversationId: newConversation._id })
      }

      return conversation
    },

    async sendReply({ dispatch, getters, rootGetters }, { promoId, message }) {
      const { selectActiveConversation } = getters

      const response = await ConversationService.sendReply({
        conversationId: selectActiveConversation._id,
        promoId,
        message
      })
      const { messageId, isWithinCelebrationTime: celebrate } = response.body.data.reply
      // Optimistically update timeline with psudeo event
      const { _id, company, customer, location } = selectActiveConversation
      const user = rootGetters['user/onlineUser']
      const loc = rootGetters['location/selectLocation'](location._id)
      const created = new Date().toISOString()
      const analyticEvent = {
        isMessagePlaceholder: true,
        _id: 'temp-' + messageId,
        type: 'outgoing-message',
        company,
        customer,
        location: {
          ...location,
          timezone: loc && loc.timezone
        },
        conversation: _id,
        message: {
          _id: messageId,
          user: user._id,
          body: message,
          type: 'outgoing',
          created
          // media: [],
        },
        user,
        created,
        __v: 0
      }
      // Committed straight away, gift card or not: the operator's own message must
      // appear the moment they send it. On the gift-card path the card's entry is
      // held back behind this event instead (timelineV2/holdGiftCardEvents).
      dispatch('timelineV2/setTempTimelineEvent', analyticEvent, { root: true })

      // The gift-card timeline event is no longer synthesized here — issueGiftCard
      // owns it, and it runs before this action.
      dispatch('timelineV2/setIsWithinCelebrationTime', celebrate, { root: true })
    },

    /**
     * Issues a gift card, separate from sendReply: the gift-card service texts the
     * guest itself, so it can't ride the reply request. No optimistic timeline event
     * — the issue response lacks the persisted card's `_id`/`last4Digits` that
     * `GiftCardSentEvent.vue` needs, so the timeline is refetched instead.
     */
    async issueGiftCard(
      { dispatch, getters },
      { amountCents, refreshTimeline = true, idempotencyKey, confirmDuplicate = false }
    ) {
      const { selectActiveConversation } = getters
      const customer = selectActiveConversation?.customer
      const companyId = selectActiveConversation?.company
      const locationId = selectActiveConversation?.location?._id

      // Always the conversation's own customer, never operator input. The id is what the
      // card is filed against — the backend no longer resolves the guest from name/phone —
      // and name/phone are still required for the guest-scoped rate-limit/idempotency
      // claims and the operator-facing echo.
      if (!customer?._id) {
        throw new Error('Cannot issue a gift card: the conversation customer has no id')
      }
      if (!customer?.name || !customer?.phone) {
        throw new Error('Cannot issue a gift card: the conversation customer has no name or phone')
      }

      // The card is funded by the terminal identity configured for this location, so
      // sending the wrong one would charge the wrong restaurant. Taken from the
      // conversation, never from the operator's current filter or selection.
      if (!companyId || !locationId) {
        throw new Error('Cannot issue a gift card: the conversation has no company or location')
      }

      const response = await GiftCardService.issue({
        amountCents,
        customerId: customer._id,
        guestContact: { name: customer.name, phone: customer.phone },
        companyId,
        locationId,
        idempotencyKey,
        // Set only after the operator was shown the card the backend named in a
        // `duplicate-issue-window` 409 and chose to fund a second one anyway.
        confirmDuplicate
      })

      // `latest: true` — the default fetch pages backwards from the oldest loaded
      // event and short-circuits entirely once a short conversation's whole history
      // is loaded, so it could never return the card that was just written.
      if (refreshTimeline) {
        await dispatch('timelineV2/fetchTimeline', { latest: true }, { root: true })
      }

      // smsDelivered false means the card WAS issued and recorded and only the
      // guest text failed (D10) — the caller surfaces that, and must not treat it
      // as a failed issuance or prompt a retry.
      return response.body.data
    },

    /**
     * A gift-card issuance failed, reported over the user's Pusher channel rather than in
     * an HTTP response.
     *
     * The request may never get one: API Gateway's integration timeout is 29s and the
     * Shift4 call cycle can run to 60s, so on a slow failure the operator has already seen
     * a 504 — or nothing — while the Lambda ran on to completion.
     *
     * `reason` decides the copy, and getting it wrong costs money. 'indeterminate' means a
     * card MAY be live at Givex with no local record, so the operator must be told to
     * check rather than retry: retrying funds a SECOND real card for the same guest.
     * Anything unrecognised is treated as indeterminate for that reason.
     */
    /**
     * The success twin of processGiftCardIssueFailed. It still clears the staged key on
     * every success — that is the half that costs money if it is skipped — but it no
     * longer announces a card that worked.
     *
     * This used to notify on every success, on the reasoning that after the gateway
     * abandons the request at 29s "no response" is what BOTH outcomes look like, so
     * silence would read as failure and invite a re-stage. The timeline covers that: the
     * card's own SENT_GIFT_CARD analytic event arrives over Pusher and renders the row,
     * so the operator is not left in silence. A notification on top of it repeats what is
     * already on screen, and its "no need to try again" half raised a doubt the operator
     * did not have.
     *
     * An undelivered text is the one outcome that survives: nothing on the timeline says
     * the guest never got the card, and the operator has to reach them directly.
     */
    async processGiftCardIssueSucceeded({ dispatch }, { smsDelivered, customerId } = {}) {
      // The card exists, so the staged key has done its job. Left behind, it would be
      // replayed by the operator's next deliberate card for this guest and the backend
      // would answer with THIS card's stored response — success reported, nothing issued.
      // Unconditional, and ahead of the notify branch: it is owed on every success.
      await dispatch('localStorage/clearGiftCardIdempotencyKey', customerId, { root: true })

      if (smsDelivered !== false) return

      Vue.notify({
        type: 'warn',
        text: 'The gift card was created, but the text to the guest failed to send. Contact them directly — do not create another card.'
      })
    },

    processGiftCardIssueFailed(_context, { reason } = {}) {
      const isRetryable = reason === 'failed'

      Vue.notify({
        type: 'error',
        text: isRetryable
          ? 'The gift card could not be created and no card was charged. You can try again.'
          : 'We could not confirm whether the gift card was created. Do NOT try again — check with support before issuing another card for this guest.'
      })
    },

    async resolveConversation({ dispatch, rootGetters }, conversationId) {
      const response = await ConversationService.markResolved({ conversationId })

      if (response.body.success !== true) {
        throw response.body
      } else {
        await dispatch('conversationChange', {
          conversationData: {
            _id: conversationId,
            resolved: true,
            resolvedBy: rootGetters['dataCore/selectActiveUserId']
          },
          trace: 'resolveConversation'
        })
      }
    },

    async unresolveConversation({ dispatch, rootGetters }, conversationId) {
      const response = await ConversationService.markUnresolved({ conversationId })

      if (response.body.success !== true) {
        throw response.body
      } else {
        await dispatch('conversationChange', {
          conversationData: {
            _id: conversationId,
            resolved: false,
            resolvedBy: null
          },
          trace: 'unresolveConversation'
        })
      }
    },

    async conversationChange({ commit, dispatch, getters }, { conversationData, trace }) {
      if (!getters.selectFilters.search) {
        let conversation = getters.selectConversationById(conversationData._id)
        if (conversation) {
          commit('SET_CONVERSATIONS', [{ ...conversation, ...conversationData }])
        } else {
          await dispatch('fetchConversation', {
            conversation: conversationData,
            trace: `conversationChange - ${trace}`
          })
        }
      }
    },

    async processConversationUpdated({ dispatch, getters }, conversation) {
      const isSearching = getters.selectIsSearching
      if (isSearching) return
      await dispatch('conversationChange', {
        conversationData: conversation,
        trace: 'processConversationUpdated'
      })
    },

    async processConversationAdded({ dispatch, getters }, conversation) {
      const isSearching = getters.selectIsSearching
      if (isSearching) return
      await dispatch('conversationChange', {
        conversationData: conversation,
        trace: 'processConversationAdded'
      })
    },

    async updateIsMobileView({ commit }, status) {
      commit('SET_IS_MOBILE_VIEW', status)
    },

    async updateShowDetailsPane({ commit }, status) {
      commit('SET_SHOW_DETAILS_PANE', status)
    },

    async updateIsWebview({ commit }, status) {
      commit('SET_IS_WEBVIEW', status)
    },

    async updateIsDeeplink({ commit }, status) {
      commit('SET_IS_DEEPLINK', status)
    },

    async saveNote({ getters, rootGetters, dispatch }, { conversationId, text, mentions }) {
      const response = await NoteService.saveNote({ conversationId, text, mentions })
      const { note } = response.body.data
      // Optimistically update timeline with psudeo event
      const { _id, company, customer, location } = getters.selectActiveConversation
      const user = rootGetters['user/onlineUser']
      const loc = rootGetters['location/selectLocation'](location._id)
      const created = new Date().toISOString()
      const analyticEvent = {
        isMessagePlaceholder: true,
        _id: 'temp-' + note._id,
        type: 'internal-note-added',
        company,
        customer,
        location: {
          ...location,
          timezone: loc && loc.timezone
        },
        conversation: _id,
        note,
        user,
        created,
        __v: 0
      }
      dispatch('timelineV2/setTempTimelineEvent', analyticEvent, { root: true })
    },

    // Stands in for the ticket-note-writer's note, which is written asynchronously and lands
    // 2-4s after the create.
    async showTicketCreatedPlaceholder({ getters, rootGetters, dispatch }, data) {
      const message = parseTicketCreatedMessage(data)
      if (!message) return
      const activeConversation = getters.selectActiveConversation
      if (!activeConversation || activeConversation._id !== message.conversationId) return

      const { _id, company, customer, location } = activeConversation
      const loc = location && rootGetters['location/selectLocation'](location._id)
      const analyticEvent = {
        isMessagePlaceholder: true,
        _id: ticketPlaceholderId(message.ticketId),
        type: 'internal-note-added',
        company,
        customer,
        location: location && {
          ...location,
          timezone: loc && loc.timezone
        },
        conversation: _id,
        note: {
          mediaParts: [{ type: 'text', text: ticketNoteText(message) }],
          ticket: { id: message.ticketId, number: message.number }
        },
        user: rootGetters['user/onlineUser'],
        created: new Date().toISOString(),
        __v: 0
      }
      dispatch('timelineV2/setTicketPlaceholder', analyticEvent, { root: true })
    },

    async generateReply(_, { conversationId }) {
      const response = await ConversationService.generateReply({ conversationId })
      return { ...response.body.data }
    },

    async fetchOpenRates({ commit }, { companyIds, dateRange }) {
      const response = await ConversationService.fetchOpenRates({ companyIds, dateRange })
      commit('SET_OPEN_RATES', response.body.data.data)
    },

    setSearchString({ commit }, searchString) {
      commit('SET_SEARCH_STRING', searchString)
    }
  }
}
